import React, { useEffect, useState } from "react";
import moment from "moment";
import { useQuery } from "@apollo/client";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { ADMIN_GROUP_ORDERS } from "../../Graphql/group-order.gql";
import {
  adminStatusLabel,
  budgetLabel,
  formatDelivery,
  serverBudget,
} from "../../services/group-order/group-order.utils";
import "./group-order.css";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const STATUS_FILTERS: Record<string, string[] | null> = {
  active: ["Open", "Ready", "CheckingOut"],
  Open: ["Open"],
  Ready: ["Ready"],
  CheckingOut: ["CheckingOut"],
  Placed: ["Placed"],
  Cancelled: ["Cancelled"],
  all: null,
};
const when = (d?: string) => (d ? moment(d).format("MMM D, h:mm A") : "—");

export const AdminGroupOrders = () => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusKey, setStatusKey] = useState("active");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, loading, error } = useQuery(ADMIN_GROUP_ORDERS, {
    variables: {
      search: search || null,
      status: STATUS_FILTERS[statusKey],
      skip: page * rowsPerPage,
      limit: rowsPerPage,
    },
    fetchPolicy: "network-only",
  });
  const rows = data?.adminGroupOrders?.data || [];
  const count = data?.adminGroupOrders?.count || 0;

  return (
    <div className="group-orders-page">
      <h2 style={{ margin: "16px 0" }}>Admin Ordering</h2>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <TextField
          size="small"
          label="Search rep, email, name or address"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="go-status">Status</InputLabel>
          <Select
            labelId="go-status"
            label="Status"
            value={statusKey}
            onChange={(e) => {
              setStatusKey(e.target.value as string);
              setPage(0);
            }}>
            <MenuItem value="active">Not placed yet</MenuItem>
            <MenuItem value="Open">Waiting / adding items</MenuItem>
            <MenuItem value="Ready">Submitted</MenuItem>
            <MenuItem value="CheckingOut">Rep checking out</MenuItem>
            <MenuItem value="Placed">Placed</MenuItem>
            <MenuItem value="Cancelled">Cancelled</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </div>
      {error && !data && (
        <Alert severity="error">We couldn't load admin orders. Refresh to try again.</Alert>
      )}
      <TableContainer>
        <Table aria-label="Admin orders" size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Rep</TableCell>
              <TableCell>Caterer</TableCell>
              <TableCell>Delivery</TableCell>
              <TableCell>Budget</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Items last added</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Placed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !data && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={24} aria-label="Loading" />
                </TableCell>
              </TableRow>
            )}
            {rows.map((go: any) => {
              const budget = serverBudget(go);
              return (
                <TableRow key={go._id} hover>
                  <TableCell>
                    <Link component={RouterLink} to={`/group-orders/${go._id}/view`}>
                      {go.label || "Admin order"}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {go.customerName}
                    <div style={{ fontSize: 12 }}>{go.customerEmail}</div>
                  </TableCell>
                  <TableCell>{go.catererName || "Not picked yet"}</TableCell>
                  <TableCell>
                    {go.street ? `${go.street}, ${go.city || ""} · ` : ""}
                    {formatDelivery(go)}
                  </TableCell>
                  <TableCell>
                    {budgetLabel(budget, (n: number) => money.format(n))}
                    {budget.isOver && <span className="group-order-marker">Over budget</span>}
                  </TableCell>
                  <TableCell>
                    {go.status === "Placed" && go.orderId ? (
                      <Link component={RouterLink} to={`/orders/${go.orderId}`}>
                        Placed
                      </Link>
                    ) : (
                      adminStatusLabel(go)
                    )}
                  </TableCell>
                  <TableCell>{when(go.createdOn)}</TableCell>
                  <TableCell>{when(go.itemsUpdatedAt)}</TableCell>
                  <TableCell>{when(go.readyAt)}</TableCell>
                  <TableCell>{when(go.placedAt)}</TableCell>
                </TableRow>
              );
            })}
            {!rows.length && !loading && !error && (
              <TableRow>
                <TableCell colSpan={10}>No admin orders match.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setPage(0);
        }}
      />
    </div>
  );
};
